import React, { useState, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

function BulkDialog({ righe, endpoint, titolo, campi, prefix, onClose, onSaved }) {
  const open = !!righe;
  const [f, setF] = useState({});
  useEffect(() => { if (open) setF({}); }, [open]);
  const mut = useMutation({
    mutationFn: async (p) => (await api.put(endpoint, p)).data,
    onSuccess: (r) => { toast.success(`${r.aggiornate} righe aggiornate`); onSaved(); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const salva = () => {
    const p = { ids: righe.map((r) => r.id) };
    for (const c of campi) {
      const v = f[c.k];
      if (v === undefined || v === "" || v === "__nessuno__") continue;
      p[c.k] = c.type === "number" ? Number(v) : c.type === "int" ? parseInt(v, 10) : String(v).trim();
    }
    if (Object.keys(p).length === 1) return toast.error("Compila almeno un campo da modificare");
    mut.mutate(p);
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid={`${prefix}-bulk-dialog`}>
        <DialogHeader>
          <DialogTitle className="font-display">{titolo} · {righe?.length || 0} righe</DialogTitle>
          <DialogDescription>Compila solo i campi da cambiare: quelli lasciati vuoti non verranno modificati.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          {campi.filter((c) => !c.visibleIf || c.visibleIf(f)).map((c) => (
            <div key={c.k} className={`space-y-1.5 ${c.full ? "col-span-2" : ""}`}>
              <Label>{c.label}</Label>
              {c.hint && <div className="text-xs text-slate-400 -mt-1">{c.hint}</div>}
              {c.type === "select" ? (
                <Select value={f[c.k] ?? "__nessuno__"} onValueChange={(v) => setF((s) => ({ ...s, [c.k]: v }))}>
                  <SelectTrigger data-testid={`${prefix}-bulk-${c.k}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__nessuno__">Non modificare</SelectItem>
                    {c.options.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <Input type={c.type === "text" ? "text" : c.type === "date" ? "date" : "number"} min={c.type === "date" ? undefined : "0"} step={c.step} placeholder="Non modificare" value={f[c.k] ?? ""} onChange={(e) => setF((s) => ({ ...s, [c.k]: e.target.value }))} data-testid={`${prefix}-bulk-${c.k}`} />
              )}
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid={`${prefix}-bulk-annulla`}>Annulla</Button>
          <Button onClick={salva} disabled={mut.isPending} data-testid={`${prefix}-bulk-salva`}>{mut.isPending ? "Salvataggio…" : "Applica a tutte"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ModificaOreBulk(props) {
  return (
    <BulkDialog
      {...props}
      endpoint="/ore-bulk"
      titolo="Modifica ore"
      prefix="ore"
      campi={[
        { k: "ore_ordinarie", label: "Ore ordinarie", hint: "solo righe Ordinario (max 8)", type: "number", step: "0.5" },
        { k: "ore_straordinarie", label: "Ore straordinarie", hint: "solo righe Straordinario", type: "number", step: "0.5" },
        { k: "luogo", label: "Luogo", type: "select", options: [{ v: "officina", l: "Officina" }, { v: "cantiere", l: "Cantiere" }] },
        { k: "note", label: "Note", type: "text" },
      ]}
    />
  );
}

export function ModificaAcquistiBulk({ isAdmin, ...props }) {
  return (
    <BulkDialog
      {...props}
      endpoint="/materiali-bulk"
      titolo="Modifica acquisti"
      prefix="mat"
      campi={[
        ...(isAdmin
          ? [
              { k: "pagamento", label: "Stato pagamento", type: "select", options: [{ v: "pagata", l: "Segna pagata" }, { v: "da_pagare", l: "Segna da pagare" }] },
              { k: "data_pagamento", label: "Data pagamento", hint: "vuoto = oggi", type: "date", visibleIf: (f) => f.pagamento === "pagata" },
            ]
          : []),
        { k: "descrizione", label: "Descrizione", type: "text", full: true },
      ]}
    />
  );
}

export function ModificaTrasferteBulk(props) {
  return (
    <BulkDialog
      {...props}
      endpoint="/trasferte-bulk"
      titolo="Modifica trasferte"
      prefix="tra"
      campi={[
        { k: "notti", label: "Notti fuori sede", type: "int", step: "1" },
        { k: "indennita_notte", label: "Indennità/notte (€)", type: "number", step: "0.5" },
        { k: "descrizione", label: "Descrizione", type: "text", full: true },
      ]}
    />
  );
}
